import Tabs from '@components/common/Tabs';
import { farmingHist, FarmlandAddReq } from '@/api/growth/farmland/type';
import PlantingMng from './PlantingMng';
import WaterMng from './WaterMng';
import FertilizerMng from '@components/page/Growth/FertilizerMng';
import ResidueMng from '@components/page/Growth/ResidueMng';
import PestMng from '@components/page/Growth/PestMng';
import TillageMng from '@components/page/Growth/TillageMng';
import EnvironmentMng from '@components/page/Growth/EnvironmentMng';
import Tooltip from '@components/common/Tooltip';
import Switch from '@components/common/Switch';

interface GrowthManagementProps {
    formData: FarmlandAddReq;
    onInputChange: (
        field: string,
        value: string | number | farmingHist[],
    ) => void;
}

const GrowthManagement = ({
    formData,
    onInputChange,
}: GrowthManagementProps) => {
    const tabs = [
        {
            id: 'planting-mng',
            label: '파종관리',
            icon: 'ri-leaf-line',
            content: (
                <PlantingMng
                    formData={formData}
                    onInputChange={onInputChange}
                />
            ),
        },
        {
            id: 'water-mng',
            label: '물관리(I)',
            icon: 'ri-contrast-drop-2-line',
            content: (
                <WaterMng formData={formData} onInputChange={onInputChange} />
            ),
        },
        {
            id: 'fertilizer-mng',
            label: '무기질비료(F)',
            icon: 'ri-flask-line',
            content: (
                <FertilizerMng
                    formData={formData}
                    onInputChange={onInputChange}
                />
            ),
        },
        {
            id: 'residue-mng',
            label: '유기질비료(R)',
            icon: 'ri-flask-fill',
            content: (
                <ResidueMng formData={formData} onInputChange={onInputChange} />
            ),
        },
        {
            id: 'pest-mng',
            label: '병해충관리(C)',
            icon: 'ri-bug-line',
            content: (
                <PestMng formData={formData} onInputChange={onInputChange} />
            ),
        },
        {
            id: 'tillage-mng',
            label: '밭갈이(T)',
            icon: 'ri-caravan-line',
            content: (
                <TillageMng formData={formData} onInputChange={onInputChange} />
            ),
        },
        {
            id: 'environment-mng',
            label: '재배환경(E)',
            icon: 'ri-sun-foggy-line',
            content: (
                <EnvironmentMng
                    formData={formData}
                    onInputChange={onInputChange}
                />
            ),
        },
    ];

    return (
        <div className="growth-management-container">
            <div className="row mb-1">
                <div className="col-md-12 d-flex align-items-center gap-4 mb-3">
                    <label className="form-label mt-3  ">
                        생육관리여부
                        <Tooltip
                            content="생육관리가 활성화 되어있는 농지만 생육예측 및 추천데이터가 생성됩니다."
                            customClass="tooltip-dark"
                            placement="top"
                        >
                            <i className="ri-question-line" />
                        </Tooltip>
                        <span className="text-danger">*</span> :
                    </label>
                    <Switch
                        id="size-lg"
                        name="toggleswitchsize"
                        checked={formData.growthFlag === 1}
                        onChange={value =>
                            onInputChange('growthFlag', value ? 1 : 0)
                        }
                    />
                </div>
            </div>

            {!formData.growthFlag && (
                <div className="row">
                    <div className="col-12">
                        <div className="card bg-primary-transparent border-0 mb-4">
                            <div className="card-body">
                                <div className="d-flex align-items-start">
                                    <div className="me-3">
                                        <div className="avatar avatar-lg bg-primary-gradient rounded-circle">
                                            <i className="ri-lightbulb-flash-line fs-4"></i>
                                        </div>
                                    </div>
                                    <div className="flex-fill">
                                        <h6 className="card-title mb-2 text-primary">
                                            <i className="ri-information-line me-1"></i>
                                            생육관리 기능 안내
                                        </h6>
                                        <p className="card-text text-muted mb-3">
                                            생육관리를 활성화하면 AI 기반의
                                            스마트 농업 서비스를 이용하실 수
                                            있습니다.
                                        </p>
                                        <div className="row g-3">
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-start">
                                                    <div className="me-2">
                                                        <span className="badge bg-success-transparent rounded-circle p-2">
                                                            <i className="ri-line-chart-line"></i>
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fs-13 fw-semibold">
                                                            생육예측 서비스
                                                        </h6>
                                                        <p className="mb-0 fs-12 text-muted">
                                                            작물의 생육 단계를
                                                            예측하고 최적의
                                                            수확시기를
                                                            안내합니다.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-start">
                                                    <div className="me-2">
                                                        <span className="badge bg-info-transparent rounded-circle p-2">
                                                            <i className="ri-file-list-3-line"></i>
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fs-13 fw-semibold">
                                                            맞춤형 추천 데이터
                                                        </h6>
                                                        <p className="mb-0 fs-12 text-muted">
                                                            관수, 시비, 병해충
                                                            관리 등 농지별 맞춤
                                                            관리 정보를
                                                            제공합니다.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-start">
                                                    <div className="me-2">
                                                        <span className="badge bg-warning-transparent rounded-circle p-2">
                                                            <i className="ri-notification-3-line"></i>
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fs-13 fw-semibold">
                                                            실시간 알림
                                                        </h6>
                                                        <p className="mb-0 fs-12 text-muted">
                                                            중요한 관리 시점에
                                                            알림을 받아 적기
                                                            영농이 가능합니다.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="d-flex align-items-start">
                                                    <div className="me-2">
                                                        <span className="badge bg-purple-transparent rounded-circle p-2">
                                                            <i className="ri-bar-chart-grouped-line"></i>
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-1 fs-13 fw-semibold">
                                                            생육 데이터 분석
                                                        </h6>
                                                        <p className="mb-0 fs-12 text-muted">
                                                            생육 이력을 분석하여
                                                            다음 작기 계획
                                                            수립을 지원합니다.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div
                                            className="alert alert-warning-transparent mt-3 mb-0"
                                            role="alert"
                                        >
                                            <div className="d-flex align-items-center">
                                                <i className="ri-error-warning-line me-2 fs-16"></i>
                                                <small>
                                                    <strong>참고:</strong>{' '}
                                                    생육관리를 비활성화하면 예측
                                                    및 추천 서비스가 중단되며,
                                                    기존 데이터는 보관됩니다.
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {formData.growthFlag === 1 && (
                <Tabs tabs={tabs} defaultActiveTab="planting-mng" />
            )}
        </div>
    );
};

export default GrowthManagement;
